import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { downloadViaCdn } from "@/shared/lib/download";
import { IconButton } from "@/shared/ui";
import { CloseIcon, DownloadIcon } from "./icons";

export interface ImageLightboxProps {
  src: string;
  alt: string;
  filename: string;
  onClose: () => void;
}

/**
 * Full-screen viewer for a single chat-generated image — opened by clicking the
 * result inline, closed via the X, Escape, or a click on the dimmed backdrop.
 * Mirrors the library lightbox: download + close in a header, the image centered
 * and `object-contain` so the whole frame is visible at full size.
 */
export function ImageLightbox({ src, alt, filename, onClose }: ImageLightboxProps) {
  const { t } = useTranslation();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col bg-canvas/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
    >
      {/* Dimmed backdrop — clicking the empty space around the image closes. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />

      <header className="relative flex items-center justify-end gap-1 px-4 py-3">
        <IconButton
          aria-label={t("images.lightbox.download")}
          title={t("images.lightbox.download")}
          onClick={() => downloadViaCdn(src, filename)}
        >
          <DownloadIcon />
        </IconButton>
        <IconButton aria-label={t("common.close")} title={t("common.close")} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </header>

      <div className="pointer-events-none relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6">
        <img
          src={src}
          alt={alt}
          className="pointer-events-auto max-h-full max-w-full rounded-lg object-contain"
        />
      </div>
    </div>,
    document.body,
  );
}
